import React, { useEffect } from "react";
import backgroundImage from "../../Elements/Composition/temp_bg.png";
import {
  allowedCharacters,
  glyphDimension,
  maxGlyphsPerColumn,
} from "../../configs/generated-page-configs";
import { loadImage } from "../../utils/image-utils";
import LottieData from "../../types/lottie";
import CartiglioData from "../../types/cartiglio";

type Props = {
  userName?: string;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  lottieData: LottieData[];
  cartigli: CartiglioData[];
};

const useHieroglyphDownloader = ({
  userName = "",
  canvasRef,
  lottieData,
  cartigli,
}: Props) => {
  useEffect(() => {
    if (lottieData.length === 0 || cartigli.length === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Imposta le dimensioni del canvas
    canvas.width = 1080; // Sostituisci con la larghezza desiderata
    canvas.height = 1920; // Sostituisci con l'altezza desiderata

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    //Carica l'immagine di sfondo
    const background = new Image();
    background.src = backgroundImage;
    background.onload = async () => {
      ctx.drawImage(background, 0, 0, canvas.width, canvas.height);

      const splitNames = userName.split(" ");
      let totalGlyphs = 0;

      let maxLength = 0;

      // Trova la lunghezza massima tra i nomi divisi
      splitNames.forEach((_name) => {
        if (_name.length > maxLength) maxLength = _name.length;
      });

      // Carica il cartiglio con la lunghezza massima
      const maxCartiglio = await loadImage(
        `/assets/cartigli-png/Cartiglio_${maxLength}.png`,
      );

      // Altezza massima del cartiglio
      const maxCartiglioHeight = maxCartiglio.height / 4; //= 1400;
      const y = (canvas.height - maxCartiglioHeight + 300) / 2;

      splitNames.forEach((_name, columnIndex) => {
        if (columnIndex > 0) {
          // Aggiungi uno spazio tra le colonne
          totalGlyphs += 1;
        }

        //let totalGlyphsInColumn = 0; // Numero totale di geroglifici nella colonna corrente

        // Carica il cartiglio specifico per il nome corrente
        loadImage(`/assets/cartigli-png/Cartiglio_${_name.length}.png`).then(
          (imgCartiglio) => {
            const cartiglioWidth = imgCartiglio.width / 4;
            const cartiglioHeight = imgCartiglio.height / 4;

            let cartiglioX = (canvas.width - cartiglioWidth) / 2;

            if (splitNames.length === 2) {
              cartiglioX += 100 * (columnIndex === 0 ? -1 : 1);
            }

            // Calcola la posizione y per centrare la colonna nel canvas
            const cartiglioY = y + (maxCartiglioHeight - cartiglioHeight);

            // Disegna il cartiglio
            ctx.drawImage(
              imgCartiglio,
              cartiglioX,
              cartiglioY,
              cartiglioWidth,
              cartiglioHeight,
            );

            // Carica e disegna gli SVG dei geroglifici per ogni carattere nel nome
            _name.split("").forEach((char, glyphIndex) => {
              if (totalGlyphs >= maxGlyphsPerColumn) {
                return; // Raggiunto il limite massimo di geroglifici per colonna
              }

              const lowerCaseChar = char.toLowerCase();

              if (allowedCharacters.includes(lowerCaseChar)) {
                // Carica e disegna il geroglifico
                loadImage(`/assets/hieroglyphs-png/${lowerCaseChar}.png`).then(
                  (glyph) => {
                    const scale = 0.9;
                    const glyphY = cartiglioY + glyphIndex * glyph.height * scale;

                    let glyphX = (canvas.width - glyph.width * scale) / 2;

                    if (splitNames.length === 2) {
                      glyphX += 100 * (columnIndex === 0 ? -1 : 1);
                    }

                    ctx.drawImage(
                      glyph,
                      glyphX,
                      glyphY + 30, // 22 pixels is the arc height of the cartiglio
                      glyph.width * scale,
                      glyph.height * scale,
                    );

                    totalGlyphs += 1;
                  },
                );
              }
            });
          },
        );
      });

      // Aggiungi testo al canvas
      ctx.fillStyle = "white";
      ctx.textAlign = "center";

      ctx.font =
        { 14: 90, 20: 80, 28: 70 }[
          userName?.length < 14 ? 14 : userName?.length < 20 ? 20 : 28
        ] + "px Atkinson Hyperlegible";

      const names = userName?.split(" ");

      names.forEach((_name, _nameIndex) => {
        let textY = 405;

        if (names.length === 2) {
          textY = 430;
          textY += 50 * (_nameIndex === 0 ? -1 : 1);
        }

        ctx.fillText(_name?.toUpperCase(), canvas.width / 2, textY);
      });
    };
  }, [canvasRef, userName, lottieData, cartigli]);

  // Funzione per il download dell'immagine
  const downloadImage = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const image = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const link = document.createElement("a");
    link.download = `${userName}.png`;
    link.href = image;
    link.click();
  };

  return { downloadImage };
};

export default useHieroglyphDownloader;
